/**
 * Settings › Warehouses — every warehouse account in one list (ACT2-1031).
 *
 * A row is a Lookout connection (Connected, or Needs attention) or a RudderStack account Lookout is
 * not connected to (Available, or Can't connect). In a sibling workspace (ACT2-1124) the accounts of
 * the organization's other workspaces are rows of the same list: Connect copies the account into this
 * workspace, then connects the copy. It runs on the shared list stack the way Settings ›
 * Accounts does — `useListControls` + `ListToolbar` + `filterSortItems` + the composite `Table` — with
 * search, a Status filter with counts, and sorts by status, name and type. Adding a new account is the
 * sticky card at the end of the list.
 *
 * Reuses the `settings.warehouses` oRPC procedures through `useWarehouseAccountList`, the list that the
 * setup panel's warehouse step and chat's connect card read too.
 */
import { retlPrivilegeWarning } from '@server/retl-schema';
import type { WarehouseProblem } from '@server/warehouse-failure';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Inbox, Plus, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { ROW_ACTIONS_COLUMN } from '@/_shared/components/list/listColumns';
import { ListNoMatches } from '@/_shared/components/list/ListStates';
import { ListToolbar } from '@/_shared/components/list/ListToolbar';
import { Table } from '@/_shared/components/Table/Table';
import type { TableColumn } from '@/_shared/components/Table/types';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/_shared/components/ui/empty';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/_shared/components/ui/sheet';
import { WarehouseProblemCallout } from '@/_shared/components/WarehouseProblemCallout/WarehouseProblemCallout';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useListControls } from '@/_shared/hooks/useListControls';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { useRudderstackConfigured } from '@/_shared/hooks/useRudderstackConfigured';
import { encodeMultiFilter, filterSortItems, type SortState } from '@/_shared/lib/list-controls';

import { AccountCopyWarning } from '../components/accountCopies/AccountCopyWarning';
import { CopyAccountDialog } from '../components/accountCopies/CopyAccountDialog';
import { ReadableWorkspacesNotice } from '../components/accountCopies/ReadableWorkspacesNotice';
import { RudderstackNotConfiguredCard } from '../components/RudderstackNotConfiguredCard';
import { SettingsCardsSkeleton } from '../components/SettingsCardsSkeleton';
import { SettingsSectionHeader } from '../components/SettingsSectionHeader';
import {
  refreshPermissionOnRefusal,
  useWarehousePermission,
} from '../components/warehouseAccount/accountCreatePermission';
import { AddWarehouseAccountDrawer } from '../components/warehouseAccount/addAccount/AddWarehouseAccountDrawer';
import { AdoptAccountDrawer, type AdoptTarget } from '../components/warehouseAccount/adopt/AdoptAccountDrawer';
import { accountDisplayName } from '../components/warehouseAccount/adopt/adoptPresentation';
import { connectionRowActions } from '../components/warehouseAccount/connectionRowActions';
import { KeyInfoLine, RetiredKeyAction } from '../components/warehouseAccount/KeyInfoLine';
import { PermissionNotice } from '../components/warehouseAccount/PermissionNotice';
import { RemovalImpactSummary } from '../components/warehouseAccount/RemovalImpactSummary';
import { RevokeAccountDialog } from '../components/warehouseAccount/RevokeAccountDialog';
import { RotateKeyDrawer } from '../components/warehouseAccount/RotateKeyDrawer';
import { testRequestFailed } from '../components/warehouseAccount/shared/testRequestFailed';
import { LookoutWarehouseEditor } from '../components/warehouseAccount/snowflake/LookoutWarehouseEditor';
import {
  COUNTING_IMPACT,
  removalImpactLoading,
  useRemovalImpact,
} from '../components/warehouseAccount/useRemovalImpact';
import { useWarehouseAccountList } from '../components/warehouseAccount/useWarehouseAccountList';
import type { WarehouseAccount } from '../components/warehouseAccount/useWarehouseAccounts';
import { WarehouseAccountDetails } from '../components/warehouseAccount/WarehouseAccountDetails';
import {
  type ConnectionRow,
  listedWarehouseRows,
  WAREHOUSE_DEFAULT_SORT,
  WAREHOUSE_DEFAULT_STATUSES,
  WAREHOUSE_DERIVE_CONFIG,
  WAREHOUSE_FILTER_KEYS,
  WAREHOUSE_SORT_KEYS,
  WAREHOUSE_SORT_OPTIONS,
  WAREHOUSE_STATUSES,
  warehouseFilterGroups,
  type WarehouseFilterKey,
  type WarehouseListRow,
  type WarehouseSortKey,
} from '../components/warehouseAccount/warehouseList';
import {
  WarehouseActionsCell,
  WarehouseNameCell,
  WarehouseStatusBadge,
  WarehouseTypeCell,
} from '../components/warehouseAccount/WarehouseListCells';
import {
  WarehouseListContext,
  type WarehouseListContextValue,
} from '../components/warehouseAccount/warehouseListContext';
import { WarehouseProjectsSection } from '../components/warehouseAccount/WarehouseProjectsSection';
import { WarehouseSyncButton } from '../components/warehouseAccount/WarehouseSyncButton';
import {
  SUPPORTED_WAREHOUSE_TYPES,
  type WarehouseTypeKey,
  warehouseTypeMeta,
} from '../components/warehouseAccount/warehouseTypes';

export function SettingsWarehouses(): React.ReactElement {
  usePageTitle('Warehouses — Settings');
  const { isAdmin, isClickHouseWarehouseEnabled, isLegacyKeyConversionEnabled, isPlatformAccountRevokeEnabled } =
    useWorkspace();

  const [addOpen, setAddOpen] = useState(false);
  const [viewAccount, setViewAccount] = useState<WarehouseAccount | null>(null);
  const [deleteAccount, setDeleteAccount] = useState<WarehouseAccount | null>(null);
  const [rotateAccount, setRotateAccount] = useState<WarehouseAccount | null>(null);
  const [revokeAccount, setRevokeAccount] = useState<WarehouseAccount | null>(null);
  const [reconnect, setReconnect] = useState<{
    connectionId: string;
    typeKey: WarehouseTypeKey;
    connectionKey: string;
    /** The connection's name, so the drawer can say what it is reconnecting. */
    name: string;
    /** ACT2-1031: the stored Lookout compute warehouse, so the reconnect setup shows its grant. */
    lookoutWarehouse: string | null;
  } | null>(null);

  const [adoptTarget, setAdoptTarget] = useState<AdoptTarget | null>(null);
  const [detachTarget, setDetachTarget] = useState<ConnectionRow | null>(null);

  // Phase 3d: the connections and the account overview stay two reads on purpose. The connections are
  // connection-first and this surface is account-first, and folding the two would make every connection
  // list re-read the workspace's platform accounts.
  const list = useWarehouseAccountList({
    onConnectionSaved: () => setAddOpen(false),
    onAdopted: () => setAdoptTarget(null),
  });
  const { connections: hook, adopt: adoptHook, organization, rows } = list;
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  // ACT2-1276: the user's RudderStack permission to create warehouse accounts decides every action
  // on this page; the Lookout role decides none. Only `allowed` opens them.
  const permission = useWarehousePermission();
  const manageBlocked = permission.blocked;
  const startReconnect = (account: WarehouseAccount): void => {
    const meta = SUPPORTED_WAREHOUSE_TYPES.find((m) => m.backendTypes.includes(account.type.toUpperCase()));
    if (meta)
      setReconnect({
        connectionId: account.id,
        typeKey: meta.key,
        connectionKey: account.connectionKey,
        name: account.name,
        lookoutWarehouse: account.lookoutWarehouse,
      });
  };
  const { accounts, isLoading } = hook;

  // ACT2-877: the accounts list's `connection_exists` refusal names the connection already covering
  // those coordinates, and this page is the only place that holds both lists. The map is by uuid
  // because that is what the server's refusal carries — a connection key embeds the host.
  const connectionNames = useMemo(
    () => Object.fromEntries(accounts.map((account) => [account.id, account.name])),
    [accounts],
  );

  // Health and ownership both read the RudderStack account a connection is bound to, which only the
  // accounts overview carries. While it is loading the rows (`list.rows`) are listed with their status
  // pending (see `WarehouseStatusBadge`); when it is unreadable every connection reads as its own row
  // alone: paused and credential-less rows are still caught, nothing is guessed.

  // Warehouse accounts are synced from RudderStack destinations, so a workspace with
  // no RudderStack connection has nothing to show — gate the tab the same way Sources does.
  const { isNotConfigured, isReadOnly, isLoading: settingsLoading } = useRudderstackConfigured();

  const controls = useListControls<WarehouseSortKey, WarehouseFilterKey>({
    sortKeys: WAREHOUSE_SORT_KEYS,
    defaultSort: WAREHOUSE_DEFAULT_SORT,
    filterKeys: WAREHOUSE_FILTER_KEYS,
    persistKey: 'settings-warehouses',
  });
  // The Status checkboxes decide which rows are listed at all — see `listedWarehouseRows`.
  const listed = useMemo(() => listedWarehouseRows(rows, controls.filters.status), [rows, controls.filters.status]);
  const visible = useMemo(
    () =>
      filterSortItems(
        listed,
        { query: controls.query, sort: controls.sort, filters: controls.filters },
        WAREHOUSE_DERIVE_CONFIG,
      ),
    [listed, controls.query, controls.sort, controls.filters],
  );
  // From every row, not `visible`: options that vanish as you narrow would rearrange under the cursor.
  // None while statuses are pending: the counts would be wrong.
  const filterGroups = useMemo(
    () => (adoptHook.isLoading ? [] : warehouseFilterGroups(rows)),
    [rows, adoptHook.isLoading],
  );
  // Every account is one the Status filter leaves out by default (all Can't connect): say so, rather
  // than "no matches" with a Clear that changes nothing.
  const hiddenByDefault = rows.length > 0 && listed.length === 0 && !controls.filters.status && !controls.query;

  // Fixed: the cells read everything that changes from `WarehouseListContext` (see there for why).
  const columns = useMemo<TableColumn<WarehouseListRow>[]>(
    () => [
      { id: 'name', header: 'Name', cell: (r) => <WarehouseNameCell row={r} /> },
      { id: 'typeLabel', header: 'Type', width: '9rem', cell: (r) => <WarehouseTypeCell row={r} /> },
      { id: 'status', header: 'Status', width: '9rem', cell: (r) => <WarehouseStatusBadge row={r} /> },
      { ...ROW_ACTIONS_COLUMN, width: '13rem', cell: (r) => <WarehouseActionsCell row={r} /> },
    ],
    [],
  );
  // ACT2-1128: a live check of a saved connection, reported in a toast. Keyed by connection key only,
  // the stored-connection path the server already supports. One run per row at a time: each click dials
  // the customer's warehouse.
  const testSavedMutation = useMutation(orpc.settings.warehouses.testConnection.mutationOptions());
  const [testingKeys, setTestingKeys] = useState<ReadonlySet<string>>(() => new Set());
  // The whole problem behind a failed row Test: the toast has room for its words only, and the support
  // code, vendor codes and details it leaves out are what support asks for (ACT2-1077).
  const [testProblem, setTestProblem] = useState<{ name: string; problem: WarehouseProblem } | null>(null);
  const failedTestToast = (name: string, text: string, problem: WarehouseProblem, id: string | number): void => {
    toast.error(text, {
      id,
      action: { label: 'Show details', onClick: () => setTestProblem({ name, problem }) },
    });
  };
  const testSaved = (a: WarehouseAccount): void => {
    if (testingKeys.has(a.connectionKey)) return;
    setTestingKeys((prev) => new Set(prev).add(a.connectionKey));
    const toastId = toast.loading(`Testing ${a.name}…`);
    // Per-call promise, not `mutate` callbacks: a second test, or leaving the page, detaches the
    // observer, and the first test's loading toast would never close.
    testSavedMutation
      .mutateAsync({ connectionKey: a.connectionKey })
      .then(
        (result) => {
          // Same reading of the result as the add drawer: the rETL write check, then the server's problem
          // (ACT2-1077), whose next step is written for a saved connection.
          const activationWarning =
            result.success && result.retlSchema ? retlPrivilegeWarning(result.retlSchema) : null;
          if (activationWarning) toast.warning(`${a.name}: ${activationWarning}`, { id: toastId });
          else if (result.success) toast.success(`${a.name}: the connection works.`, { id: toastId });
          else if (result.problem) {
            // Which warehouse failed, when a Snowflake test dials two (ACT2-1031), as the callout shows it.
            const stage = result.problem.stage ? `${result.problem.stage}: ` : '';
            failedTestToast(
              a.name,
              `${a.name}: ${stage}${result.problem.message} ${result.problem.nextStep}`,
              result.problem,
              toastId,
            );
          }
          // A server that predates ACT2-1077 sends only its sentence (a rolling deploy).
          else toast.error(`${a.name}: test failed. ${result.error ?? 'Unknown error.'}`, { id: toastId });
        },
        (err: unknown) => {
          refreshPermissionOnRefusal(queryClient, orpc, err);
          const problem = testRequestFailed(err);
          failedTestToast(a.name, `${a.name}: ${problem.message} ${problem.nextStep}`, problem, toastId);
        },
      )
      .finally(() =>
        setTestingKeys((prev) => {
          const next = new Set(prev);
          next.delete(a.connectionKey);
          return next;
        }),
      );
  };
  // ACT2-1340: Run orientation tests the connection first and queues the run only when the test passes,
  // so a run never starts on a connection that cannot sign in. One request per row at a time. The status
  // dot moves to "Orienting" when the list refetches.
  const runOrientationMutation = useMutation(orpc.settings.warehouses.runOrientation.mutationOptions());
  const [orientingIds, setOrientingIds] = useState<ReadonlySet<string>>(() => new Set());
  const runOrientation = (a: WarehouseAccount): void => {
    if (orientingIds.has(a.id)) return;
    setOrientingIds((prev) => new Set(prev).add(a.id));
    const toastId = toast.loading(`Testing ${a.name}…`);
    testSavedMutation
      .mutateAsync({ connectionKey: a.connectionKey })
      .then(
        async (result) => {
          if (!result.success) {
            if (result.problem) {
              const stage = result.problem.stage ? `${result.problem.stage}: ` : '';
              failedTestToast(
                a.name,
                `${a.name}: ${stage}${result.problem.message} ${result.problem.nextStep}`,
                result.problem,
                toastId,
              );
            } else toast.error(`${a.name}: test failed. ${result.error ?? 'Unknown error.'}`, { id: toastId });
            return;
          }
          await runOrientationMutation.mutateAsync({ connectionId: a.id });
          toast.success(`${a.name}: orientation started. Lookout is mapping this warehouse in the background.`, {
            id: toastId,
          });
          void queryClient.invalidateQueries({ queryKey: orpc.settings.warehouses.listAccounts.key() });
        },
        (err: unknown) => {
          refreshPermissionOnRefusal(queryClient, orpc, err);
          const problem = testRequestFailed(err);
          failedTestToast(a.name, `${a.name}: ${problem.message} ${problem.nextStep}`, problem, toastId);
        },
      )
      .catch((err: unknown) => {
        // The run request itself was refused (no longer retryable, team notes off): its message is the reason.
        toast.error(`${a.name}: ${err instanceof Error ? err.message : 'orientation could not start.'}`, {
          id: toastId,
        });
      })
      .finally(() =>
        setOrientingIds((prev) => {
          const next = new Set(prev);
          next.delete(a.id);
          return next;
        }),
      );
  };
  const chooseAccount = (a: WarehouseAccount): void =>
    setAdoptTarget({ connectionKey: a.connectionKey, connectionName: a.name, type: a.type });
  const listContext: WarehouseListContextValue = {
    hook: adoptHook,
    organization,
    connectionNames,
    manageBlocked,
    rudderstackReadOnly: isReadOnly,
    onReconnect: startReconnect,
    onConnectAccount: chooseAccount,
    connectionActions: (row) =>
      connectionRowActions(row, {
        manageBlocked,
        rudderstackReadOnly: isReadOnly,
        isLegacyKeyConversionEnabled,
        isPlatformAccountRevokeEnabled,
        onView: setViewAccount,
        onRotate: setRotateAccount,
        onRevoke: setRevokeAccount,
        onDelete: setDeleteAccount,
        onDetach: setDetachTarget,
        onChooseAccount: chooseAccount,
        onTest: testSaved,
        isAdmin,
        onRunOrientation: runOrientation,
        statusPending: adoptHook.isLoading,
      }),
  };

  // The confirm waits until the dialog shows what uses the account.
  const deleteImpact = useRemovalImpact(deleteAccount?.id ?? null);
  const detachImpact = useRemovalImpact(detachTarget?.account.id ?? null);

  const confirmDetach = (): void => {
    if (!detachTarget) return;
    adoptHook.detach({ connectionKey: detachTarget.account.connectionKey });
    setDetachTarget(null);
  };

  const confirmDelete = (): void => {
    if (!deleteAccount) return;
    hook.removeConnection(deleteAccount);
    setDeleteAccount(null);
    setViewAccount(null);
  };

  const viewMeta = viewAccount ? warehouseTypeMeta(viewAccount.type) : null;

  // Refresh pulls newly-added RudderStack destinations and accounts in; it sits beside Filter and Sort.
  const syncControl = (
    <WarehouseSyncButton
      blockedReason={manageBlocked}
      isSyncing={hook.syncMutation.isPending}
      onSync={() => hook.syncMutation.mutate({})}
    />
  );

  const description = 'Connect a warehouse account so Lookout can query your data.';

  // Wait for settings.get before deciding — otherwise the not-configured gate reads false while
  // loading and we'd render the full warehouses UI (and fire account queries) prematurely.
  if (settingsLoading) {
    return (
      <>
        <SettingsSectionHeader title="Warehouses" description={description} />
        <SettingsCardsSkeleton />
      </>
    );
  }

  if (isNotConfigured) {
    return (
      <>
        <SettingsSectionHeader title="Warehouses" description={description} />
        <RudderstackNotConfiguredCard description="Sign in with RudderStack to link a workspace, and your warehouse destinations will sync automatically." />
      </>
    );
  }

  // The primary action sits in the header, as on Settings › Accounts.
  const addButton = (
    <ActionTooltip show={manageBlocked !== null} message={manageBlocked ?? ''}>
      <Button
        type="button"
        variant="primary"
        Icon={<Plus className="size-4" />}
        disabled={manageBlocked !== null}
        onClick={() => setAddOpen(true)}
        label="Add account"
      />
    </ActionTooltip>
  );
  const header = <SettingsSectionHeader title="Warehouses" description={description} action={addButton} />;
  // The list is ready once the connections are; RudderStack accounts join it when the overview, the sibling status
  // and, in a sibling workspace, the organization's list land (`list.isLoading`). With no connection to show, only
  // those can tell an empty workspace from a loading one.
  const listReady = !isLoading && (!list.isLoading || accounts.length > 0);

  return (
    <>
      {header}

      <PermissionNotice permission={permission} className="mb-4" />

      {adoptHook.unavailableProblem !== undefined && (
        <div className="mb-4">
          <WarehouseProblemCallout problem={adoptHook.unavailableProblem} />
        </div>
      )}

      {organization.unavailable !== null && (
        <Callout variant="error" className="mb-4">
          Could not read the accounts of your organization’s other workspaces just now, so only this workspace’s
          accounts are listed. Refresh to try again.
        </Callout>
      )}

      {/* ACT2-1124 (D18): accounts of other workspaces show only for the workspaces in the General tab's list. */}
      {list.showReadableWorkspacesNotice && <ReadableWorkspacesNotice className="mb-4" />}

      {!listReady ? (
        <SettingsCardsSkeleton />
      ) : rows.length === 0 ? (
        <Empty className="border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Inbox />
            </EmptyMedia>
            <EmptyTitle>No warehouse accounts yet</EmptyTitle>
            <EmptyDescription>
              Add a warehouse account to start querying your data. Accounts created in RudderStack also appear here.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            {/* The toolbar holding Refresh renders only with rows, so the empty state carries it too:
                an account just created in RudderStack is otherwise unreachable from here. */}
            <div className="flex items-center gap-2">
              <ActionTooltip show={manageBlocked !== null} message={manageBlocked ?? ''}>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={manageBlocked !== null}
                  onClick={() => setAddOpen(true)}
                  Icon={<Plus />}
                  label="Add your first account"
                />
              </ActionTooltip>
              {syncControl}
            </div>
          </EmptyContent>
        </Empty>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <ListToolbar
            query={controls.query}
            onQueryChange={controls.setQuery}
            searchPlaceholder="Search warehouses…"
            sortOptions={WAREHOUSE_SORT_OPTIONS}
            sort={controls.sort}
            onSortChange={(next) => controls.setSort(next as SortState<WarehouseSortKey>)}
            defaultSort={WAREHOUSE_DEFAULT_SORT}
            filterGroups={filterGroups}
            filters={controls.filters}
            onFilterChange={(key, value) => controls.setFilter(key as WarehouseFilterKey, value)}
            onClearFilters={controls.clearFilters}
            onClearAll={controls.clearAll}
            totalCount={listed.length}
            resultCount={visible.length}
            itemNoun={{ singular: 'warehouse account', plural: 'warehouse accounts' }}
            actions={syncControl}
          />
          {hiddenByDefault ? (
            <Empty className="border">
              <EmptyHeader>
                <EmptyTitle>No warehouse account Lookout can connect yet</EmptyTitle>
                <EmptyDescription>
                  {rows.length === 1
                    ? 'One RudderStack account is listed under Can’t connect.'
                    : `${rows.length} RudderStack accounts are listed under Can’t connect.`}{' '}
                  Add an account to start querying your data.
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    controls.setFilter('status', encodeMultiFilter(WAREHOUSE_STATUSES, WAREHOUSE_DEFAULT_STATUSES))
                  }
                  label="Show them"
                />
              </EmptyContent>
            </Empty>
          ) : visible.length === 0 ? (
            <ListNoMatches
              noun="warehouse accounts"
              onClear={() => {
                controls.setQuery('');
                controls.clearFilters();
              }}
            />
          ) : (
            <WarehouseListContext.Provider value={listContext}>
              {/* The settings shell gives this tab a fixed height (`SettingsShell`), so the table takes the rest
                  of it: its header stays in view and its rows scroll, as on the Audiences page. */}
              <div className="min-h-0 flex-1">
                <Table<WarehouseListRow>
                  data={visible}
                  getRowId={(r) => r.id}
                  columns={columns}
                  fixedLayout
                  fillHeight
                  rowClassName={(r) => (r.status === 'cant_connect' ? 'text-muted-foreground [&_img]:opacity-50' : '')}
                />
              </div>
            </WarehouseListContext.Provider>
          )}
        </div>
      )}

      <CopyAccountDialog organization={organization} />

      <AdoptAccountDrawer
        target={adoptTarget}
        hook={adoptHook}
        canAct={manageBlocked === null}
        onOpenChange={(open) => {
          if (!open) setAdoptTarget(null);
        }}
      />

      <AddWarehouseAccountDrawer
        open={addOpen || reconnect !== null}
        onOpenChange={(open) => {
          setAddOpen(open);
          if (!open) setReconnect(null);
        }}
        blockedReason={manageBlocked}
        hook={hook}
        reconnect={reconnect}
      />

      <RotateKeyDrawer
        account={rotateAccount}
        onOpenChange={(open) => {
          if (!open) setRotateAccount(null);
        }}
      />
      <RevokeAccountDialog
        account={revokeAccount}
        onOpenChange={(open) => {
          if (!open) setRevokeAccount(null);
        }}
      />

      {/* Read-only account view */}
      <Sheet open={!!viewAccount} onOpenChange={(open) => !open && setViewAccount(null)}>
        <SheetContent side="right" showCloseButton={false} className="w-full gap-0 sm:max-w-drawer">
          {viewAccount && viewMeta && (
            <>
              <SheetHeader className="border-b border-border p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-3">
                    <viewMeta.Icon className="size-9 shrink-0" />
                    <span className="flex min-w-0 flex-col">
                      <SheetTitle className="truncate text-sm font-medium text-foreground">
                        {viewAccount.name}
                      </SheetTitle>
                      <SheetDescription className="text-xs text-muted-foreground">{viewMeta.label}</SheetDescription>
                    </span>
                  </span>
                  <SheetClose asChild>
                    <Button type="button" variant="tertiary" iconOnly aria-label="Close" Icon={<X />} />
                  </SheetClose>
                </div>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto p-4">
                {/* ACT2-1124: a copy of another workspace's account: the shared login, and an IAM role's trust policy. */}
                <div className="mb-4 grid gap-3 empty:hidden">
                  <AccountCopyWarning accountId={viewAccount.rsAccountId} />
                </div>
                <WarehouseAccountDetails type={viewAccount.type} config={viewAccount.config} />
                {viewAccount.type.toUpperCase() === 'SNOWFLAKE' && (
                  <LookoutWarehouseEditor
                    connectionId={viewAccount.id}
                    lookoutWarehouse={viewAccount.lookoutWarehouse}
                    computeWarehouse={viewAccount.computeWarehouse}
                    role={viewAccount.config && 'role' in viewAccount.config ? (viewAccount.config.role ?? null) : null}
                    blockedReason={manageBlocked}
                  />
                )}
                {/* "Remove the old key" lives here and on the rotate success screen, never on the list. */}
                {viewAccount.keySource && !viewAccount.pausedReason && (
                  <p className="mt-4">
                    <KeyInfoLine connectionId={viewAccount.id} />
                  </p>
                )}
                {manageBlocked === null && viewAccount.keySource && !viewAccount.pausedReason && (
                  <RetiredKeyAction connectionId={viewAccount.id} />
                )}
                <WarehouseProjectsSection accountKey={viewAccount.accountKey} />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* A failed row Test, in full */}
      <Dialog open={testProblem !== null} onOpenChange={(open) => !open && setTestProblem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Test failed: {testProblem?.name}</DialogTitle>
            <DialogDescription>Include the support code if you contact support.</DialogDescription>
          </DialogHeader>
          {testProblem && <WarehouseProblemCallout problem={testProblem.problem} />}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setTestProblem(null)} label="Close" />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detach confirmation */}
      <Dialog open={detachTarget !== null} onOpenChange={(open) => !open && setDetachTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Detach account</DialogTitle>
            <DialogDescription>
              This unbinds <span className="font-medium">{detachTarget?.account.name}</span> from the RudderStack
              account{' '}
              <span className="font-medium">
                {detachTarget?.bound ? accountDisplayName(detachTarget.bound) : 'it reads from'}
              </span>
              .
              {/* Detach is not free, and the screen has to say so: after it the connection has no
                  credential and refuses until an account is connected again. The RudderStack account
                  itself is untouched, and the connection is never deleted. */}{' '}
              The RudderStack account is not deleted, but the connection can't run queries until you connect an account
              to it again.
            </DialogDescription>
          </DialogHeader>
          {detachTarget && <RemovalImpactSummary accountId={detachTarget.account.id} path="detach" />}
          <PermissionNotice permission={permission} />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setDetachTarget(null)} label="Cancel" />
            <ActionTooltip show={removalImpactLoading(detachImpact)} message={COUNTING_IMPACT}>
              <Button
                type="button"
                variant="primary-destructive"
                loading={adoptHook.detachPending}
                disabled={manageBlocked !== null || removalImpactLoading(detachImpact)}
                onClick={confirmDetach}
                label="Detach account"
              />
            </ActionTooltip>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteAccount} onOpenChange={(open) => !open && setDeleteAccount(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete account</DialogTitle>
            <DialogDescription>
              {deleteAccount?.type.toUpperCase() === 'CLICKHOUSE' && !isClickHouseWarehouseEnabled ? (
                <>
                  This removes the stored connection for <span className="font-medium">{deleteAccount.name}</span>. This
                  workspace cannot reconnect this ClickHouse account until ClickHouse create is enabled. This can't be
                  undone.
                </>
              ) : (
                <>
                  This removes the stored connection for <span className="font-medium">{deleteAccount?.name}</span>.
                  This can't be undone.
                  {/* Phase 3b: a platform connection's credential lives in RudderStack, so
                      deleting the connection here leaves that account — and the credential in it —
                      standing. Nothing else on this screen says so. */}
                  {deleteAccount?.credentialSource === 'platform' && (
                    <>
                      {' '}
                      Its RudderStack account and the credential stored there are not deleted — remove the account in
                      RudderStack if you want the credential gone.
                    </>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {/* In PR 1 this is the only warning: no item list or page labels what the delete broke. */}
          {deleteAccount && <RemovalImpactSummary accountId={deleteAccount.id} path="delete" />}
          <PermissionNotice permission={permission} />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setDeleteAccount(null)} label="Cancel" />
            <ActionTooltip show={removalImpactLoading(deleteImpact)} message={COUNTING_IMPACT}>
              <Button
                type="button"
                variant="primary-destructive"
                disabled={manageBlocked !== null || removalImpactLoading(deleteImpact)}
                onClick={confirmDelete}
                label="Delete account"
              />
            </ActionTooltip>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
